import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { addWeeks, isSameDay } from "date-fns";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice } from "../../_layout";
import Workout from "../../Firestore/Workout";
import Exercise from "../../Firestore/Exercise";
import { useTranslation } from "react-i18next";
import ToggleSwitch from "../../Components/ToggleSwitch";
import Tooltip from "../../Components/Tooltip";
import { useToast } from "../../contexts/ToastContext";
import { BlockPlanificationConstants } from "../../../Resources/Constants";

const DuplicateWeekModal = observer(
  ({ isVisible, onClose, block, sourceWeek }) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useDevice();
    const { showToast } = useToast();

    const [selectedWeeks, setSelectedWeeks] = useState([]);
    const [copyActualToTarget, setCopyActualToTarget] = useState(false);
    const [closeHovered, setCloseHovered] = useState(false);
    const [hoveredWeek, setHoveredWeek] = useState(null);
    const [clearAllHovered, setClearAllHovered] = useState(false);
    const [confirmHovered, setConfirmHovered] = useState(false);
    const [selectAllHovered, setSelectAllHovered] = useState(false);

    const toUppercase = (value) =>
      typeof value === "string" ? value.toUpperCase() : value;

    const weeksWithExercises = useMemo(() => {
      const weeks = new Set();
      if (!block?.workouts) return weeks;
      block.workouts.forEach((workout) => {
        const week = workout?.week;
        if (!week) return;
        const exercises = Array.isArray(workout?.exercises)
          ? workout.exercises
          : [];
        const exercisesToAdd = Array.isArray(workout?.exercisesToAdd)
          ? workout.exercisesToAdd
          : [];
        if (exercises.length > 0 || exercisesToAdd.length > 0) {
          weeks.add(week);
        }
      });
      return weeks;
    }, [block?.workouts]);

    useEffect(() => {
      if (!isVisible) return;

      const suggestedWeek =
        typeof sourceWeek === "number" ? sourceWeek + 1 : undefined;
      if (
        typeof suggestedWeek === "number" &&
        block?.totalWeeks &&
        suggestedWeek <= block.totalWeeks &&
        !weeksWithExercises.has(suggestedWeek)
      ) {
        setSelectedWeeks([suggestedWeek]);
      } else {
        setSelectedWeeks([]);
      }
      setCopyActualToTarget(false);
    }, [isVisible, sourceWeek, block?.totalWeeks, weeksWithExercises]);

    useEffect(() => {
      if (selectedWeeks.length !== 1 && copyActualToTarget) {
        setCopyActualToTarget(false);
      }
    }, [selectedWeeks.length, copyActualToTarget]);

    const weeks = useMemo(() => {
      if (!block?.totalWeeks) return [];
      return Array.from({ length: block.totalWeeks }, (_, i) => i + 1);
    }, [block?.totalWeeks]);

    const sourceWeekWorkouts = useMemo(() => {
      if (!block?.workouts || !sourceWeek) return [];
      return block.workouts.filter((workout) => workout?.week === sourceWeek);
    }, [block?.workouts, sourceWeek]);

    const protocolSlotsInSourceWeek = useMemo(() => {
      const slots = [];
      if (!block?.protocols || !sourceWeek) return slots;
      block.protocols.forEach((protocol) => {
        if (protocol?.protocolWeek !== sourceWeek) return;
        if (protocol?.protocolSlot && !slots.includes(protocol.protocolSlot)) {
          slots.push(protocol.protocolSlot);
        }
      });
      return slots;
    }, [block?.protocols, sourceWeek]);

    const takenProtocolSlots = useMemo(() => {
      const slots = new Set();
      if (!sourceWeekWorkouts.length) return slots;
      sourceWeekWorkouts.forEach((workout) => {
        const exercises = Array.isArray(workout?.exercises)
          ? workout.exercises
          : [];
        const exercisesToAdd = Array.isArray(workout?.exercisesToAdd)
          ? workout.exercisesToAdd
          : [];

        exercises.forEach((exercise) => {
          if (exercise?.protocolSlot) {
            slots.add(exercise.protocolSlot);
          }
        });

        exercisesToAdd.forEach((exercise) => {
          if (exercise?.protocolSlot) {
            slots.add(exercise.protocolSlot);
          }
        });
      });
      return slots;
    }, [sourceWeekWorkouts]);

    const missingProtocolSlots = useMemo(() => {
      if (protocolSlotsInSourceWeek.length === 0) return [];
      return protocolSlotsInSourceWeek.filter(
        (slot) => !takenProtocolSlots.has(slot),
      );
    }, [protocolSlotsInSourceWeek, takenProtocolSlots]);

    const missingProtocolSlotLabels = useMemo(() => {
      if (missingProtocolSlots.length === 0) return [];
      return missingProtocolSlots.map((slot) => t(slot));
    }, [missingProtocolSlots, t]);

    const hasUnaddedProtocolSlots = missingProtocolSlots.length > 0;

    const hasAccessoryExercises = useMemo(() => {
      if (!sourceWeekWorkouts.length) return false;
      return sourceWeekWorkouts.some((workout) =>
        (workout?.exercises || []).some(
          (exercise) => exercise?.protocolSlot == null,
        ),
      );
    }, [sourceWeekWorkouts]);

    const missingAccessoryExercises =
      sourceWeekWorkouts.length > 0 && !hasAccessoryExercises;

    const hasSelectedWeekProtocols = useMemo(() => {
      if (!block?.protocols || selectedWeeks.length === 0) return false;
      const selected = new Set(selectedWeeks);
      return block.protocols.some(
        (protocol) =>
          protocol?.protocolSlot && selected.has(protocol.protocolWeek),
      );
    }, [block?.protocols, selectedWeeks]);

    const persistBlockChange = () => {
      const normalizedId = String(block?.id ?? "").toLowerCase();
      if (block?.client) {
        block.client.updateClientTransactionData(normalizedId);
        block.client.startOrResetBatchTimer();
      } else if (block?.coach) {
        block.coach.updateCoachTransactionBlocks(normalizedId);
        block.coach.startOrResetBatchTimer();
      }
    };

    const getWeekLabel = (week) => {
      if (!week) return "";
      return t("weekAbbr-string", { week });
    };

    const toggleWeekSelection = (week) => {
      if (week === sourceWeek || weeksWithExercises.has(week)) return;
      setSelectedWeeks((prev) =>
        prev.includes(week)
          ? prev.filter((w) => w !== week)
          : [...prev, week].sort((a, b) => a - b),
      );
    };

    const clearTargetWeekWorkouts = (targetWeek) => {
      if (!block?.workouts) return;
      block.workouts = block.workouts.filter(
        (workout) => workout?.week !== targetWeek,
      );
    };

    const getAllClientWorkouts = () => {
      // Coach blocks never have workout dates, so no date conflict checking needed
      if (block?.coach) return [];
      if (!block?.client) return [];
      if (Array.isArray(block.client.blocks)) {
        return block.client.blocks.flatMap((clientBlock) =>
          Array.isArray(clientBlock?.workouts) ? clientBlock.workouts : [],
        );
      }
      return block.client.getWorkouts?.() ?? [];
    };

    const duplicateWeekToTarget = (targetWeek, mode = "target") => {
      const sourceWorkouts = (block?.workouts || [])
        .filter((w) => w.week === sourceWeek)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const existingTargetWorkouts = (block?.workouts || []).filter(
        (w) => w.week === targetWeek,
      );
      const baseOrder =
        existingTargetWorkouts.length > 0
          ? Math.max(...existingTargetWorkouts.map((w) => w.order ?? 0)) + 1
          : 0;

      // Coach blocks never have workout dates, so skip date conflict checking
      const isCoachBlock = !!block?.coach;
      const allClientWorkouts = isCoachBlock ? [] : getAllClientWorkouts();

      sourceWorkouts.forEach((workout, workoutIndex) => {
        const weeksToAdd = targetWeek - sourceWeek;
        const sourceDate = workout?.date ? new Date(workout.date) : null;
        const shiftedDate =
          sourceDate && Number.isInteger(weeksToAdd)
            ? addWeeks(sourceDate, weeksToAdd)
            : null;

        // Skip date conflict checking for coach blocks (they never have dates)
        const hasDateConflict =
          !isCoachBlock &&
          shiftedDate &&
          allClientWorkouts.some((w) => {
            if (!w?.date) return false;
            return isSameDay(new Date(w.date), shiftedDate);
          });

        const newWorkout = new Workout(
          uuidv4(),
          hasDateConflict ? null : shiftedDate,
          workout?.name ?? "",
          baseOrder + workoutIndex,
          targetWeek,
          false,
          block,
          [],
        );
        newWorkout.setProperty("hasChanged", true);

        const sortedExercises = Array.isArray(workout?.exercises)
          ? [...workout.exercises].sort(
              (a, b) => (a?.order ?? 0) - (b?.order ?? 0),
            )
          : [];

        const usedOrderValues = new Set();

        sortedExercises.forEach((exercise, exerciseIndex) => {
          if (!exercise) return;

          const protocolExercise = block?.protocols?.find(
            (p) =>
              p.protocolSlot &&
              p.protocolSlot === exercise.protocolSlot &&
              p.protocolWeek === targetWeek &&
              !p.isSecondaryProtocol,
          );

          // Skip secondary protocol exercises if primary protocol exists for target week
          if (exercise.isSecondaryProtocol && protocolExercise) {
            return; // Skip this exercise - primary protocol will be created from block.protocols
          }

          let exerciseOrder =
            typeof exercise.order === "number" ? exercise.order : exerciseIndex;
          while (usedOrderValues.has(exerciseOrder)) {
            exerciseOrder += 1;
          }
          usedOrderValues.add(exerciseOrder);

          const newExercise = new Exercise(
            undefined,
            exercise.name,
            exercise.originalName,
            exercise.notes,
            exerciseOrder,
            true,
            exerciseIndex === 0,
            newWorkout,
            [],
            [],
            undefined,
            undefined,
            exercise.link,
          );

          newExercise.protocolSlot = exercise.protocolSlot;
          newExercise.protocolText = exercise.protocolText;
          newExercise.isSecondaryProtocol = exercise.isSecondaryProtocol;

          if (protocolExercise) {
            newExercise.name = protocolExercise.name;
            newExercise.originalName = protocolExercise.originalName;
            newExercise.notes = protocolExercise.notes;
            newExercise.protocolText = protocolExercise.protocolText;
            newExercise.link = protocolExercise.link;

            // For protocol exercises, always use "target" mode to get progression from dashboard
            protocolExercise.series?.forEach((serie) =>
              newExercise.copySerie(serie, false, "target"),
            );
            protocolExercise.attributes?.forEach((attribute) =>
              newExercise.copyAttribute(attribute),
            );

            if (protocolExercise.secondProtocolExercise) {
              let secondOrder = exerciseOrder + 1;
              while (usedOrderValues.has(secondOrder)) {
                secondOrder += 1;
              }
              usedOrderValues.add(secondOrder);

              const secondExercise = new Exercise(
                undefined,
                protocolExercise.secondProtocolExercise.name,
                protocolExercise.secondProtocolExercise.originalName,
                "",
                secondOrder,
                true,
                false,
                null,
                [],
                [],
                null,
                block,
                protocolExercise.secondProtocolExercise.link,
              );

              secondExercise.protocolSlot = protocolExercise.protocolSlot;
              secondExercise.protocolText =
                protocolExercise.secondProtocolExercise.protocolText;
              secondExercise.isSecondaryProtocol = true;

              // For secondary protocol exercises, always use "target" mode to get progression from dashboard
              protocolExercise.secondProtocolExercise?.series.forEach(
                (oldSerie) => {
                  secondExercise.copySerie(oldSerie, false, "target");
                },
              );

              protocolExercise.secondProtocolExercise?.attributes.forEach(
                (oldAttribute) => {
                  secondExercise.copyAttribute(oldAttribute);
                },
              );

              secondExercise.workout = newWorkout;
              newWorkout.exercises.push(secondExercise);
            }
          } else {
            exercise.series?.forEach((serie) =>
              newExercise.copySerie(serie, false, mode),
            );
            exercise.attributes?.forEach((attribute) =>
              newExercise.copyAttribute(attribute),
            );
          }

          newWorkout.exercises.push(newExercise);
        });

        block?.workouts?.push(newWorkout);
      });
    };

    const handleDuplicate = () => {
      if (!block || !sourceWeek || selectedWeeks.length === 0) return;
      const targets = selectedWeeks.filter(
        (week) =>
          week !== sourceWeek &&
          block?.totalWeeks &&
          week >= 1 &&
          week <= block.totalWeeks &&
          !weeksWithExercises.has(week),
      );
      if (targets.length === 0) return;

      runInAction(() => {
        targets.forEach((targetWeek) => {
          clearTargetWeekWorkouts(targetWeek);
          duplicateWeekToTarget(
            targetWeek,
            copyActualToTarget ? "targetToActual" : "target",
          );
        });
      });
      persistBlockChange();

      const sourceWeekLabel = t("weekWithNumber-string", { week: sourceWeek });
      const targetWeekLabels = targets
        .map((week) => t("weekWithNumber-string", { week }))
        .join(", ");

      showToast("duplicateWeekToastTitle-string", {
        type: "info",
        title: "duplicateWeekToastTitle-string",
        description: "duplicateWeekToastDescription-string",
        duration: BlockPlanificationConstants.TOAST_DURATION_MS,
        variables: {
          sourceWeek: sourceWeekLabel,
          targetWeeks: targetWeekLabels,
        },
      });

      onClose?.();
    };

    const selectableWeeks = useMemo(
      () =>
        weeks.filter(
          (week) => week !== sourceWeek && !weeksWithExercises.has(week),
        ),
      [weeks, sourceWeek, weeksWithExercises],
    );
    const hasSelectableWeeks = selectableWeeks.length > 0;
    const allSelectableSelected =
      hasSelectableWeeks &&
      selectableWeeks.every((week) => selectedWeeks.includes(week));
    const someSelectableSelected = selectableWeeks.some((week) =>
      selectedWeeks.includes(week),
    );
    const isSelectAllIndeterminate =
      hasSelectableWeeks && someSelectableSelected && !allSelectableSelected;

    const toggleSelectAllWeeks = () => {
      if (!hasSelectableWeeks) return;
      setSelectedWeeks(() =>
        allSelectableSelected ? [] : [...selectableWeeks],
      );
    };

    const canCopyActual = selectedWeeks.length === 1;
    const hasSelection = selectedWeeks.length > 0;
    const hasWarningBanner =
      hasUnaddedProtocolSlots || missingAccessoryExercises;
    const shouldShowInfoBanner = hasWarningBanner || hasSelectedWeekProtocols;
    const warningCount =
      (hasUnaddedProtocolSlots ? 1 : 0) + (missingAccessoryExercises ? 1 : 0);
    const shouldUseWarningBullets = warningCount > 0;

    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={onClose}
            style={GlobalStyles.touchableBackground}
          />
          <View style={styles.modalView}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              onMouseEnter={() => !isMobile && setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <Ionicons
                name="close"
                size={22}
                color={closeHovered ? Colors.blackColor : Colors.grayColor}
              />
            </TouchableOpacity>

            <View style={{ gap: 8, marginBottom: 16 }}>
              <Text style={styles.title}>
                {toUppercase(
                  t("duplicateWeekWithNumber-string", { week: sourceWeek }),
                )}
              </Text>
            </View>

            <View style={{ gap: 8, marginBottom: 12 }}>
              <Text style={styles.sectionLabel}>
                {t("duplicateWeekSelectFutureHint-string")}
              </Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={true}
                style={styles.weekScrollView}
                contentContainerStyle={styles.weekScrollContent}
              >
                <View style={styles.weekGridContainer}>
                  <View style={styles.weekGrid}>
                    <View style={styles.weekHeaderRow}>
                      <View style={styles.weekHeaderCell} />
                      {weeks.map((week) => {
                        const hasExercises = weeksWithExercises.has(week);
                        const isCurrentWeek = week === sourceWeek;
                        const isDisabled = isCurrentWeek || hasExercises;
                        return (
                          <View
                            key={`week-header-${week}`}
                            style={styles.weekHeaderCell}
                          >
                            <Text
                              style={[
                                styles.weekHeaderText,
                                isDisabled
                                  ? styles.weekHeaderTextDisabled
                                  : styles.weekHeaderTextActive,
                              ]}
                            >
                              {getWeekLabel(week)}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                    <View style={styles.weekCheckboxRow}>
                      <View style={styles.weekCheckboxCell}>
                        <Tooltip
                          content={
                            allSelectableSelected
                              ? "unselectAll-string"
                              : "selectAll-string"
                          }
                          placement="top"
                          disabled={!hasSelectableWeeks}
                        >
                          <TouchableOpacity
                            style={[
                              styles.selectAllButton,
                              !hasSelectableWeeks &&
                                styles.selectAllButtonDisabled,
                              hasSelectableWeeks &&
                                selectAllHovered &&
                                styles.selectAllButtonHovered,
                            ]}
                            disabled={!hasSelectableWeeks}
                            onPress={toggleSelectAllWeeks}
                            onMouseEnter={() =>
                              !isMobile && setSelectAllHovered(true)
                            }
                            onMouseLeave={() => setSelectAllHovered(false)}
                          >
                            <Ionicons
                              name={
                                allSelectableSelected
                                  ? "checkmark-done"
                                  : "checkmark-done-outline"
                              }
                              size={18}
                              color={
                                !hasSelectableWeeks
                                  ? Colors.grayColor
                                  : allSelectableSelected ||
                                      isSelectAllIndeterminate
                                    ? Colors.bubbleColor
                                    : Colors.grayColor
                              }
                              style={
                                isSelectAllIndeterminate ? { opacity: 0.5 } : {}
                              }
                            />
                          </TouchableOpacity>
                        </Tooltip>
                      </View>
                      {weeks.map((week) => {
                        const hasExercises = weeksWithExercises.has(week);
                        const isSelected = selectedWeeks.includes(week);
                        const isHovered = hoveredWeek === week;
                        const isCurrentWeek = week === sourceWeek;
                        const isDisabled = isCurrentWeek || hasExercises;
                        const shouldShowTooltip =
                          hasExercises && !isCurrentWeek;

                        return (
                          <View key={week} style={styles.weekCheckboxCell}>
                            <Tooltip
                              content="duplicateWeekBlockedWeekTooltip-string"
                              placement="top"
                              disabled={!shouldShowTooltip}
                            >
                              <TouchableOpacity
                                style={styles.touchableCheckbox}
                                disabled={isDisabled}
                                onPress={() => toggleWeekSelection(week)}
                                onMouseEnter={() =>
                                  !isMobile && setHoveredWeek(week)
                                }
                                onMouseLeave={() => setHoveredWeek(null)}
                              >
                                {isDisabled ? (
                                  <View style={styles.blockedCheckbox}>
                                    <Ionicons
                                      name="checkmark"
                                      size={12}
                                      color={Colors.whiteColor}
                                    />
                                  </View>
                                ) : isSelected ? (
                                  <View style={styles.selectedCheckbox}>
                                    <Ionicons
                                      name="checkmark"
                                      size={12}
                                      color={Colors.whiteColor}
                                    />
                                  </View>
                                ) : (
                                  <View
                                    style={[
                                      styles.unselectedCheckbox,
                                      isHovered &&
                                        styles.unselectedCheckboxHovered,
                                    ]}
                                  />
                                )}
                              </TouchableOpacity>
                            </Tooltip>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </View>
              </ScrollView>
            </View>

            <View style={styles.optionsContainer}>
              <View style={styles.optionToggleRow}>
                <ToggleSwitch
                  value={copyActualToTarget}
                  onChange={() => setCopyActualToTarget((prev) => !prev)}
                  disabled={!canCopyActual}
                />
                <View style={styles.optionTextContainer}>
                  <Text
                    style={[
                      styles.optionText,
                      !canCopyActual && styles.optionTextDisabled,
                    ]}
                  >
                    {t("duplicateWeekCopyActualLabel-string")}
                  </Text>
                  <Text
                    style={[
                      styles.optionHint,
                      !canCopyActual && styles.optionHintDisabled,
                      {
                        maxWidth:
                          (block.totalWeeks > 9 && i18n.language === "es") ||
                          (block.totalWeeks > 7 && i18n.language === "en")
                            ? "auto"
                            : i18n.language === "es"
                              ? 400
                              : 300,
                      },
                    ]}
                  >
                    {canCopyActual
                      ? t("duplicateWeekCopyActualHelper-string", {
                          week: sourceWeek,
                        })
                      : t("duplicateWeekSingleWeekOnly-string")}
                  </Text>
                </View>
              </View>

              {shouldShowInfoBanner && (
                <View
                  style={
                    hasWarningBanner
                      ? styles.warningBanner
                      : styles.blockedBanner
                  }
                >
                  <View
                    style={
                      hasWarningBanner
                        ? styles.warningBannerIcon
                        : styles.blockedBannerIcon
                    }
                  >
                    <Ionicons
                      name={
                        hasWarningBanner
                          ? "help-circle-outline"
                          : "information-circle-outline"
                      }
                      size={14}
                      color={
                        hasWarningBanner
                          ? Colors.warningTextColor
                          : Colors.darkgrayColor
                      }
                    />
                  </View>
                  <View
                    style={
                      hasWarningBanner
                        ? styles.warningBannerContent
                        : styles.blockedBannerContent
                    }
                  >
                    {hasWarningBanner ? (
                      <View style={styles.warningList}>
                        <Text style={styles.warningQuestionText}>
                          {t("duplicateWeekWarningQuestion-string", {
                            week: sourceWeek,
                          })}
                        </Text>
                        {hasUnaddedProtocolSlots && (
                          <Text style={styles.warningText}>
                            {shouldUseWarningBullets ? "• " : ""}
                            {t(
                              missingProtocolSlotLabels.length === 1
                                ? "duplicateWeekWarningMissingSlotSingular-string"
                                : "duplicateWeekWarningMissingSlotsPlural-string",
                              {
                                slots: missingProtocolSlotLabels.join(", "),
                                week: sourceWeek,
                              },
                            )}
                          </Text>
                        )}
                        {missingAccessoryExercises && (
                          <Text style={styles.warningText}>
                            {shouldUseWarningBullets ? "• " : ""}
                            {t("duplicateWeekWarningNoAccessory-string")}
                          </Text>
                        )}
                      </View>
                    ) : (
                      <Text style={styles.blockedBannerText}>
                        {t("duplicateWeekInfo-string")}
                      </Text>
                    )}
                  </View>
                </View>
              )}
            </View>

            <View style={styles.actionsRow}>
              <TouchableOpacity
                style={[
                  styles.secondaryButton,
                  !hasSelection && styles.secondaryButtonDisabled,
                  hasSelection &&
                    clearAllHovered &&
                    styles.secondaryButtonHovered,
                ]}
                disabled={!hasSelection}
                onPress={() => setSelectedWeeks([])}
                onMouseEnter={() => !isMobile && setClearAllHovered(true)}
                onMouseLeave={() => setClearAllHovered(false)}
              >
                <Text
                  style={[
                    styles.secondaryText,
                    !hasSelection && styles.secondaryTextDisabled,
                  ]}
                >
                  {t("clearAll-string")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  !hasSelection && { opacity: 0.6 },
                  hasSelection && confirmHovered && styles.primaryButtonHovered,
                ]}
                disabled={!hasSelection}
                onPress={handleDuplicate}
                onMouseEnter={() => !isMobile && setConfirmHovered(true)}
                onMouseLeave={() => setConfirmHovered(false)}
              >
                <Text style={styles.primaryText}>
                  {t("duplicateWeekConfirm-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  },
);

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 24,
    width: "auto",
    maxWidth: "90%",
    alignSelf: "center",
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    cursor: "pointer",
  },
  title: {
    ...Fonts.bold18,
    color: Colors.blackColor,
  },
  subtitle: {
    fontSize: 14,
    color: Colors.darkgrayColor,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    marginTop: 8,
  },
  weekScrollView: {
    maxHeight: 96,
  },
  weekScrollContent: {
    paddingBottom: 0,
    paddingHorizontal: 6,
    flexGrow: 1,
    justifyContent: "center",
  },
  weekGridContainer: {
    minWidth: "100%",
    alignItems: "center",
  },
  weekGrid: {
    flexDirection: "column",
  },
  weekHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
    marginTop: 8,
  },
  weekHeaderCell: {
    width: 36,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 4,
  },
  weekHeaderText: {
    fontSize: 10,
    fontWeight: "600",
    textAlign: "center",
  },
  weekHeaderTextActive: {
    color: Colors.blackColor,
  },
  weekHeaderTextDisabled: {
    color: Colors.grayColor,
  },
  weekCheckboxRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  weekCheckboxCell: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 4,
  },
  touchableCheckbox: {
    padding: 6,
  },
  selectedCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  unselectedCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
  },
  unselectedCheckboxHovered: {
    borderColor: Colors.bubbleColor,
  },
  blockedCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: Colors.grayColor,
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.5,
  },
  optionsContainer: {
    marginTop: 8,
    gap: 8,
  },
  optionToggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    paddingVertical: 4,
  },
  optionTextContainer: {
    flex: 1,
    gap: 4,
  },
  optionText: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  optionTextDisabled: {
    color: Colors.grayColor,
  },
  optionHint: {
    fontSize: 11,
    color: Colors.grayColor,
    lineHeight: 14,
  },
  optionHintDisabled: {
    color: Colors.mediumGray,
  },
  blockedBanner: {
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayColor,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  blockedBannerIcon: {
    paddingTop: 1,
  },
  blockedBannerContent: {
    flex: 1,
    gap: 0,
  },
  blockedBannerText: {
    fontSize: 11,
    lineHeight: 15,
    color: Colors.mediumGray,
  },
  warningBanner: {
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: Colors.warningBackgroundColor,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  warningBannerIcon: {
    paddingTop: 1,
  },
  warningBannerContent: {
    flex: 1,
    gap: 0,
  },
  warningList: {
    gap: 4,
  },
  warningQuestionText: {
    fontSize: 11,
    // fontWeight: "600",
    lineHeight: 16,
    color: Colors.warningTextColor,
  },
  warningText: {
    fontSize: 11,
    lineHeight: 15,
    color: Colors.warningTextColor,
  },
  selectAllButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
  },
  selectAllButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  selectAllButtonDisabled: {
    opacity: 0.6,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 24,
  },
  secondaryButton: {
    height: 36,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
  },
  secondaryButtonDisabled: {
    opacity: 0.5,
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryText: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  secondaryTextDisabled: {
    color: Colors.grayColor,
  },
  primaryButton: {
    height: 36,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.whiteColor,
  },
});

export default DuplicateWeekModal;
